/**
 * Confluence integration detail page (foundation - status only).
 *
 * The connection pill, domain, checked-at and error all come from the cached
 * `confluence` run summary (no live Confluence call on load). "Run health check"
 * is the only button that kicks live traffic. There is no Data panel or browse
 * tab yet - content sync (spaces/pages) lands in phase 2, at which point this
 * becomes a `ConfluenceLayout` + tabs like Freshdesk. Until then it renders its
 * own `AdminPageHeader` (no shared layout wrapper).
 */
import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

import AdminPageHeader from '../../../../../components/admin/shared/AdminPageHeader'
import { ConfluenceIcon } from '../../../../../components/SystemIcons'
import { useDataFetching } from '../../../../../hooks/useDataFetching'
import { useNotify } from '../../../../../components/shared/ToastProvider'
import {
  integrationsApi,
  IntegrationRun,
  IntegrationStatus,
} from '../../../../../services/admin/integrationsApi'
import IntegrationRunsTable from '../shared/IntegrationRunsTable'
import TableResultsToolbar from '../../../../../components/shared/TableResultsToolbar'
import { relativeTime } from '../shared/relativeTime'

const KEY = 'confluence'

// Leads the Connection card: a big icon + word so the health state reads at a
// glance, ahead of the domain + details below. (Same pattern as FreshdeskStatus.)
const ConnectionHero: React.FC<{ ok?: boolean | null }> = ({ ok }) => {
  if (ok == null) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <HelpOutlineIcon sx={{ color: 'text.disabled' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          Never checked
        </Typography>
      </Stack>
    )
  }
  const Icon = ok ? CheckCircleIcon : ErrorIcon
  const color = ok ? 'success.main' : 'error.main'
  const label = ok ? 'Connected' : 'Connection failed'
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Icon sx={{ color }} />
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>
        {label}
      </Typography>
    </Stack>
  )
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack direction="row" spacing={1}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Stack>
)

const ConfluenceStatus: React.FC = () => {
  const notify = useNotify()
  const [triggering, setTriggering] = useState(false)
  const [runsPage, setRunsPage] = useState(1)
  const [runsPerPage, setRunsPerPage] = useState(25)

  const {
    data: status,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useDataFetching<IntegrationStatus>({
    fetchFn: () => integrationsApi.getIntegrationStatus(KEY),
    dependencies: [],
  })

  const { data: runs, refetch: refetchRuns } = useDataFetching<IntegrationRun[]>({
    fetchFn: () => integrationsApi.listIntegrationRuns(KEY, 50),
    dependencies: [],
  })

  const health = status?.health ?? null
  const allRuns = runs ?? []
  const pagedRuns = allRuns.slice(
    (runsPage - 1) * runsPerPage,
    runsPage * runsPerPage,
  )

  const runHealthCheck = async () => {
    setTriggering(true)
    try {
      await integrationsApi.triggerIntegrationRun(KEY)
      notify.showSuccess('Health check started')
      // The run completes in the background; refetch shortly after.
      setTimeout(() => {
        refetchStatus()
        refetchRuns()
      }, 1500)
    } catch (err: any) {
      if (err?.response?.status === 409) {
        notify.showInfo('A health check is already running')
      } else {
        notify.showError('Failed to start health check')
      }
    } finally {
      setTriggering(false)
    }
  }

  return (
    <Box>
      <AdminPageHeader
        pageKey="confluenceIntegration"
        sx={{ mb: 2 }}
        iconOverride={<ConfluenceIcon size={24} />}
      />

      {statusLoading && !status ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Connection</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    triggering ? <CircularProgress size={16} /> : <RefreshIcon />
                  }
                  onClick={runHealthCheck}
                  disabled={triggering}
                >
                  Run health check
                </Button>
              </Stack>

              <ConnectionHero ok={health?.ok} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                {status?.domain || '-'}
              </Typography>

              <Stack spacing={1}>
                <Field label="Last checked">{relativeTime(health?.checked_at)}</Field>
                <Field label="Scheduled sync">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      variant="outlined"
                      color={status?.enabled ? 'success' : 'default'}
                      label={status?.enabled ? 'Enabled' : 'Disabled'}
                    />
                    {!status?.enabled && (
                      <Typography variant="caption" color="text.secondary">
                        Manual sync only
                      </Typography>
                    )}
                  </Stack>
                </Field>
                <Field label="Connection">Read-only</Field>
                {health?.error && (
                  <Field label="Error">
                    <Typography variant="body2" color="error">
                      {health.error}
                    </Typography>
                  </Field>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Recent runs</Typography>
                <TableResultsToolbar
                  page={runsPage}
                  perPage={runsPerPage}
                  total={allRuns.length}
                  onPageChange={setRunsPage}
                  onPerPageChange={(n) => {
                    setRunsPerPage(n)
                    setRunsPage(1)
                  }}
                />
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <IntegrationRunsTable runs={pagedRuns} />
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default ConfluenceStatus
